import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AppButtonComponent } from 'src/app/shared/components/app-button/app-button.component';
import { AppInputComponent, InputType } from 'src/app/shared/components/app-input/app-input.component';
import { AppInputCustomComponent, InputMaskTypes } from 'src/app/shared/components/app-input-custom/app-input-custom.component';
import { AppSelectInputComponent, SelectModel } from 'src/app/shared/components/app-select-input/app-select-input.component';
import { AlertService, AlertType } from 'src/app/core/services/alert.service';
import { CPFPipe } from "../../../shared/pipe/cpf.pipe";
import { CPFService } from 'src/app/core/services/cpf.service';
import { MemberService } from 'src/app/core/services/member.service';
import { Router } from '@angular/router';
import { EMPTY, Observable, Subject, catchError, switchMap, takeUntil, tap } from 'rxjs';
import { PlanPaymentsService } from 'src/app/core/services/plan-payments.service';
import { DependentsAmountModel } from 'src/app/shared/models/planPaymentsModel';

export interface DependentToContract {
  id?: string;
  name: string;
  documentType: number;
  cpf: string;
  document: string;
  gender: string;
  birthDate: string;
  phone: string;
  email: string;
  relationshipDegreeId?: string;
  dependentAmount?: number;
  alredyExists: boolean;
  configAmount?: number;
  originMemberId: string;
}

export function toDependentData(dep: DependentToContract) {
  const birthDate = dep.birthDate
    ? (dep.birthDate.includes('-')
      ? dep.birthDate.substring(0, 10)
      : dep.birthDate.split('/').reverse().join('-'))
    : null;

  return {
    name: dep.name,
    documentType: dep.documentType,
    cpf: dep.cpf,
    gender: dep.gender,
    email: dep.email,
    phone: dep.phone,
    birthDate,
    relationshipDegreeId: dep.relationshipDegreeId && dep.relationshipDegreeId !== 'null' ? dep.relationshipDegreeId : null,
    originMemberId: dep.originMemberId || null,
    dependentAmount: dep.configAmount ?? dep.dependentAmount ?? 0,
    alredyExists: dep.alredyExists ?? false
  };
}

export interface MemberReadModel {
  id: string;
  name: string;
  cpf: string;
  birthDate: string;
  documentType: number;
  gender: string;
  memberPhoneTO: { number: string };
  memberEmailTO: { email: string };
}

@Component({
  selector: 'app-dependentes-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppButtonComponent,
    AppInputComponent,
    AppInputCustomComponent,
    AppSelectInputComponent,
    CPFPipe
  ],
  templateUrl: './dependentes-modal.component.html',
  styleUrl: './dependentes-modal.component.scss'
})
export class DependentesModalComponent implements OnInit, OnDestroy {
  @Input() modalId: string = 'dependentes-modal';
  @Input() qtyDependents: number = 0;
  @Input() initialDependents: DependentToContract[] = [];
  @Input() relationshipDegrees: SelectModel[] = [];
  @Input() planPaymentId: string = '';
  @Input() operationType: string = 'subscription';

  @Output() dependentsUpdated = new EventEmitter<DependentToContract[]>();
  @Output() modalClose = new EventEmitter<void>();

  InputTypeEnum = InputType;
  InputMaskTypesEnum = InputMaskTypes;

  dependentForm = new FormGroup({
    id: new FormControl<string | null>(null),
    name: new FormControl<string>({ value: '', disabled: true }, [Validators.required, Validators.pattern('^[a-zA-ZÀ-ÿ]{3,}( {1,2}[a-zA-ZÀ-ÿ]{2,}){1,}$')]),
    documentType: new FormControl<number>(1, Validators.required),
    cpf: new FormControl<string>(''),
    document: new FormControl<string>('', [Validators.minLength(9), Validators.maxLength(9)]),
    gender: new FormControl<string>({ value: '', disabled: true }, Validators.required),
    birthDate: new FormControl<string>('', Validators.required),
    phone: new FormControl<string>(''),
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    relationshipDegreeId: new FormControl<string | null>(null),
    alredyExists: new FormControl<boolean>(false)
  });

  dependentsList: DependentToContract[] = [];
  editingIndex: number | null = null;
  submitted = false;
  validUser: boolean = false;
  validEmail: boolean = false;
  validPhone: boolean = true;
  calculatingAmounts: boolean = false;
  amountsCalculated: boolean = true;
  totalDependentsAmount: number = 0;

  docTypesList: SelectModel[] = [
    { code: '1', name: 'CPF' },
    { code: '2', name: 'Passaporte' }
  ];

  sexOptions: SelectModel[] = [
    { code: 'M', name: 'Masculino' },
    { code: 'F', name: 'Feminino' },
    { code: 'I', name: 'Não informado' }
  ];

  buttonAddOptions = {
    buttonText: 'Adicionar',
    buttonSize: 'btn btn-sm py-2 px-4 btn-primary',
    borderRadius: '25px',
  };

  buttonUpdateOptions = {
    buttonText: 'Atualizar',
    buttonSize: 'btn btn-sm py-2 px-4 btn-primary',
    borderRadius: '25px',
  };

  buttonCancelOptions = {
    buttonText: 'Cancelar',
    buttonSize: 'btn btn-sm py-2 px-4 btn-secondary',
    borderRadius: '25px',
  };

  buttonConfirmOptions = {
    buttonText: 'Confirmar Dependentes',
    buttonSize: 'btn btn-sm py-2 px-4 w-100 btn-primary',
    borderRadius: '25px',
  };

  private memberId: string;
  private readonly recalculate$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private alertService: AlertService,
    private cpfService: CPFService,
    private router: Router,
    private readonly memberService: MemberService,
    private readonly planPaymentsService: PlanPaymentsService
  ) {
    this.memberId = this.memberService.getMemberId();
    if (!this.memberId) {
      this.router.navigate(['/cadastro-socio']);
    }
  }

  ngOnInit(): void {
    this.dependentsList = [...this.initialDependents];
    this.totalDependentsAmount = this.sumConfigAmounts();

    this.recalculate$
      .pipe(
        switchMap(() => this.fetchAmounts()),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.dependentForm.get('documentType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      if (value == 1) {
        this.dependentForm.get('cpf')?.setValidators([Validators.required]);
        this.dependentForm.get('document')?.setValidators(null);
        this.dependentForm.get('document')?.setValue('');
        this.dependentForm.get('name')?.disable();
        this.dependentForm.get('gender')?.disable();
      } else if (value == 2) {
        this.dependentForm.get('document')?.setValidators([Validators.required]);
        this.dependentForm.get('cpf')?.setValidators(null);
        this.dependentForm.get('cpf')?.setValue('');
        this.dependentForm.get('name')?.enable();
        this.dependentForm.get('gender')?.enable();
      }

      this.dependentForm.updateValueAndValidity();
    });

    this.dependentForm.get('cpf')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.dependentForm.get('document')?.setValue(value);
    });

    this.dependentForm.get('email')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.validEmail = false;
    });

    this.dependentForm.get('phone')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.validPhone = false;
      if (!value) {
        this.validPhone = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onEmailChange() {
    if (!this.dependentForm.get('email')?.valid) {
      return;
    }

    const email = this.dependentForm.get('email')?.value;
    if (!email) {
      return;
    }

    this.memberService.getEmailAvailability(email).subscribe({
      next: (res) => {
        if (!res.available) {
          this.alertService.showAlert(AlertType.WARNING, "E-mail pertence a um usuário cadastrado. Tente novamente.");
          this.validEmail = false;
          this.dependentForm.get('email')?.setValue('');
        } else {
          this.validEmail = true;
        }
      },
      error: (err) => {
        console.error(err);
        this.validEmail = true;
      }
    });
  }

  public onPhoneChange() {
    if (!this.dependentForm.get('phone')?.valid) {
      return;
    }

    const phone = this.dependentForm.get('phone')?.value;
    if (!phone) {
      this.validPhone = true;
      return;
    }

    this.memberService.getPhoneAvailability(phone).subscribe({
      next: (res) => {
        if (!res.available) {
          this.alertService.showAlert(AlertType.WARNING, "Telefone pertence a um usuário cadastrado. Tente novamente.");
          this.validPhone = false;
          this.dependentForm.get('phone')?.setValue('');
        } else {
          this.validPhone = true;
        }
      },
      error: (err) => {
        console.error(err);
        this.validPhone = true;
      }
    });
  }

  onSearchCPF() {
    const isPassport = this.dependentForm.get('documentType')?.value == 2;
    const documentControl = this.dependentForm.get(isPassport ? 'document' : 'cpf');
    const cpf = documentControl?.value || '';
    const birthDate = this.dependentForm.get('birthDate')?.value?.replaceAll('/', '') || '';

    if (cpf?.length < 9 || documentControl?.invalid) {
      return;
    }

    this.dependentForm.disable();
    this.memberService.getDependentDataByCpf(cpf, this.memberId).subscribe({
      next: (res: MemberReadModel) => {
        this.validUser = true;

        if (res) {
          this.dependentForm.patchValue({
            name: res.name,
            gender: res.gender,
            birthDate: res.birthDate,
            email: res.memberEmailTO?.email,
            phone: res.memberPhoneTO?.number
          });

          this.validEmail = true;
          this.validPhone = true;
          this.dependentForm.get('relationshipDegreeId')?.enable();
          this.dependentForm.setValidators(null);
          this.dependentForm.updateValueAndValidity();
          return;
        }

        this.dependentForm.enable();

        if (isPassport) {
          return;
        }

        this.dependentForm.get('name')?.disable();
        this.dependentForm.get('gender')?.disable();

        if (cpf?.length === 11 && birthDate?.length === 8) {
          this.validateCpfWithReceita(cpf, birthDate);
        }
      }, error: (err) => {
        console.error(err);
        this.validUser = false;
        this.dependentForm.get('cpf')?.setValue('');
        this.dependentForm.get('document')?.setValue('');
        this.dependentForm.enable();
        if (!isPassport) {
          this.dependentForm.get('name')?.disable();
          this.dependentForm.get('gender')?.disable();
        }
      }
    });
  }

  private validateCpfWithReceita(cpf: string, birthDate: string): void {
    if (cpf?.length !== 11 || birthDate?.length !== 8) {
      return;
    }

    this.dependentForm.disable();

    // Permitir testes com qualquer CPF em localhost ou no ambiente de desenvolvimento
    if (window.location.hostname === 'localhost' || window.location.hostname === 'sociotorcedordev.efuti.com.br') {
      this.alertService.showAlert(AlertType.INFO, 'Ambiente de desenvolvimento: Validação de CPF ignorada.');
      this.dependentForm.enable();
      this.dependentForm.get('name')?.enable();
      this.dependentForm.get('gender')?.enable();
      return;
    }

    this.alertService.showAlert(AlertType.INFO, 'Aguarde, validando dados do dependente...');
    this.cpfService.searchCPF(cpf, birthDate).subscribe({
      next: (res) => {
        if (res.code === '0' && res.status === 'OK') {
          this.dependentForm.get('name')?.setValue(res.nome.toUpperCase());
          this.dependentForm.get('gender')?.setValue(res.genero.sexo);

          this.dependentForm.enable();

          if (this.dependentForm.get('gender')?.value != null && this.dependentForm.get('gender')?.value !== "") {
            this.dependentForm.get('gender')?.disable();
          }

          if (this.dependentForm.get('name')?.value != null && this.dependentForm.get('name')?.value !== "") {
            this.dependentForm.get('name')?.disable();
          }

          this.alertService.showAlert(AlertType.SUCCESS, 'Dados validados com sucesso.');
        } else {
          this.alertService.showAlert(AlertType.WARNING, res.message);

          if (res.code > '2' && res.code < '9') {
            this.dependentForm.enable();
            this.dependentForm.get('name')?.enable();
            this.dependentForm.get('gender')?.enable();
          } else if (['1', '2', '9'].includes(res.code)) {
            this.dependentForm.enable();
            this.dependentForm.get('name')?.disable();
            this.dependentForm.get('gender')?.disable();
          }
        }
      },
      error: (err) => {
        console.error(err);
        this.alertService.showAlert(AlertType.WARNING, "Validação de CPF indisponível. Preencha os campos manualmente.");
        this.dependentForm.enable();
        this.dependentForm.get('name')?.enable();
        this.dependentForm.get('gender')?.enable();
      },
    });
  }

  validFields(): boolean {
    return this.dependentForm.valid && this.validUser && this.validEmail && this.validPhone;
  }

  addOrUpdateDependent() {
    this.submitted = true;

    if (this.dependentForm.invalid) {
      return;
    }

    // Validar limite de dependentes (apenas ao adicionar novo)
    if (this.editingIndex === null && this.dependentsList.length >= this.qtyDependents) {
      this.alertService.showAlert(
        AlertType.WARNING,
        `Este plano permite no máximo ${this.qtyDependents} dependente(s)`
      );
      return;
    }

    const data: DependentToContract = this.dependentForm.getRawValue() as DependentToContract;
    data.cpf = data.document;
    data.originMemberId = '0aeb171a-1dae-4847-9dcf-23524c32ec49'; // Portal

    data.name = data.name.trim().toUpperCase();

    const duplicated = this.dependentsList.some((d, index) =>
      index !== this.editingIndex && this.isSameDependent(d, data)
    );
    if (duplicated) {
      this.alertService.showAlert(AlertType.WARNING, 'Já existe um dependente com este documento ou e-mail.');
      return;
    }

    // Converter data do formato brasileiro (DD/MM/YYYY) para ISO (YYYY-MM-DD)
    if (data.birthDate) {
      data.birthDate = this.convertDateToISO(data.birthDate);
    }

    if (this.editingIndex !== null) {
      // Atualizar dependente existente
      this.dependentsList[this.editingIndex] = data;
      this.alertService.showAlert(AlertType.SUCCESS, 'Dependente atualizado com sucesso');
      this.editingIndex = null;
    } else {
      // Adicionar novo dependente
      this.dependentsList.push(data);
      this.alertService.showAlert(AlertType.SUCCESS, 'Dependente adicionado com sucesso');
    }

    this.dependentForm.reset();
    this.dependentForm.enable();
    this.dependentForm.get('name')?.disable();
    this.dependentForm.get('gender')?.disable();
    this.dependentForm.patchValue({ documentType: 1 });
    this.submitted = false;
    this.resetValidations();

    this.recalculateAmounts();
  }

  private isSameDependent(a: DependentToContract, b: DependentToContract): boolean {
    const sameDocument = !!a.cpf && a.cpf === b.cpf;
    const sameEmail = !!a.email && !!b.email && a.email.toLowerCase() === b.email.toLowerCase();
    return sameDocument || sameEmail;
  }

  private resetValidations(): void {
    this.validUser = false;
    this.validEmail = false;
    this.validPhone = true;
  }

  private recalculateAmounts(): void {
    this.recalculate$.next();
  }

  private fetchAmounts(): Observable<unknown> {
    if (this.dependentsList.length === 0) {
      this.calculatingAmounts = false;
      this.amountsCalculated = true;
      this.totalDependentsAmount = 0;
      return EMPTY;
    }

    if (!this.planPaymentId) {
      this.calculatingAmounts = false;
      this.amountsCalculated = false;
      return EMPTY;
    }

    const dependents = [...this.dependentsList];
    this.calculatingAmounts = true;
    this.amountsCalculated = false;

    return this.planPaymentsService
      .calculateDependentsAmount(this.planPaymentId, {
        memberId: this.memberId,
        operationType: this.operationType,
        dependents: dependents.map(toDependentData)
      })
      .pipe(
        tap((res: DependentsAmountModel) => {
          // O backend devolve os itens na mesma ordem em que foram enviados
          this.dependentsList = dependents.map((dep, index) => ({
            ...dep,
            configAmount: Number(res.dependents?.[index]?.amount ?? 0)
          }));
          this.totalDependentsAmount = Number(res.chargeAmount ?? 0);
          this.calculatingAmounts = false;
          this.amountsCalculated = true;
        }),
        catchError((err) => {
          console.error(err);
          this.calculatingAmounts = false;
          this.amountsCalculated = false;
          this.alertService.showAlert(
            AlertType.DANGER,
            'Não foi possível calcular o valor dos dependentes. Tente novamente.'
          );
          return EMPTY;
        })
      );
  }

  private sumConfigAmounts(): number {
    return this.dependentsList.reduce((total, dep) => total + (dep.configAmount || 0), 0);
  }

  private convertDateToISO(date: string): string {
    // Se já está no formato ISO (YYYY-MM-DD), retorna
    if (date.includes('-')) {
      return date;
    }

    return date.split('/').reverse().join('-');
  }

  removeDependent(index: number) {
    if (this.isEditing()) {
      return;
    }

    this.dependentsList.splice(index, 1);
    this.alertService.showAlert(AlertType.SUCCESS, 'Dependente removido');
    this.recalculateAmounts();
  }

  editDependent(index: number) {
    const dependent = this.dependentsList[index];
    if (!dependent || dependent.alredyExists) {
      return;
    }
    this.dependentForm.patchValue({
      ...dependent,
      documentType: dependent.documentType as any,
      birthDate: dependent.birthDate.split('-').reverse().join('/')
    });
    this.editingIndex = index;
    this.submitted = false;

    this.validUser = true;
    this.validEmail = true;
    this.validPhone = true;
  }

  cancelEdit() {
    this.dependentForm.reset();
    this.dependentForm.patchValue({ documentType: 1 });
    this.editingIndex = null;
    this.submitted = false;
    this.resetValidations();
  }

  isEditing(): boolean {
    return this.editingIndex !== null;
  }

  getTotalDependentsAmount(): number {
    return this.totalDependentsAmount;
  }

  isLimitReached(): boolean {
    return this.dependentsList.length >= this.qtyDependents;
  }

  getRelationshipDegreeName(id: string): string {
    const degree = this.relationshipDegrees.find(r => r.code === id);
    return degree ? degree.name : '-';
  }

  getDocumentTypeName(type: number): string {
    const docType = this.docTypesList.find(d => d.code === type.toString());
    return docType ? docType.name : '-';
  }

  confirmAndClose() {
    if (this.isEditing()) {
      this.alertService.showAlert(
        AlertType.WARNING,
        'Conclua ou cancele a edição do dependente antes de confirmar.'
      );
      return;
    }

    if (this.dependentsList.length < this.qtyDependents) {
      this.alertService.showAlert(
        AlertType.WARNING,
        `Você deve adicionar ${this.qtyDependents} dependente(s). Atualmente você tem ${this.dependentsList.length}.`
      );
      return;
    }

    if (this.dependentsList.length > this.qtyDependents) {
      this.alertService.showAlert(
        AlertType.WARNING,
        `Este plano permite no máximo ${this.qtyDependents} dependente(s). Remova ${this.dependentsList.length - this.qtyDependents} para continuar.`
      );
      return;
    }

    if (this.calculatingAmounts) {
      return;
    }

    if (!this.amountsCalculated) {
      if (!this.planPaymentId) {
        this.alertService.showAlert(
          AlertType.WARNING,
          'Não foi possível calcular o valor dos dependentes: plano de pagamento não selecionado.'
        );
        return;
      }

      this.recalculateAmounts();
      return;
    }

    this.dependentsUpdated.emit(this.dependentsList);
    this.closeModal();
  }

  closeModal() {
    this.dependentForm.reset();
    this.dependentForm.patchValue({ documentType: 1 });
    this.editingIndex = null;
    this.submitted = false;
    this.modalClose.emit();

    // Fechar modal usando Bootstrap
    const modalElement = document.getElementById(this.modalId);
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  openModal() {
    this.dependentsList = [...this.initialDependents];
    this.dependentForm.reset();
    this.dependentForm.enable();
    this.dependentForm.get('name')?.disable();
    this.dependentForm.get('gender')?.disable();
    this.dependentForm.patchValue({ documentType: 1 });
    this.editingIndex = null;
    this.submitted = false;
    this.resetValidations();
    this.totalDependentsAmount = this.sumConfigAmounts();
    this.recalculateAmounts();
  }

  onDocumentTypeChange() {
    this.dependentForm.get('document')?.reset();
  }
}
